import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Extend AxiosInstance to include customRequest method
interface ExtendedAxiosInstance extends AxiosInstance {
  customRequest<T = any>(url: string, config?: any): Promise<T>;
  transcribeVoice(audio: Blob): Promise<{ transcript: string; confidence?: number; detected_language?: string }>;
  getUnmappedSources(): Promise<any>;
  getCustomers(params?: any): Promise<any>;
  createCustomer(data: any): Promise<any>;
  getCustomer(id: string): Promise<any>;
  updateCustomer(id: string, data: any): Promise<any>;
  getCustomerSources(id: string): Promise<any>;
  deleteCustomer(id: string): Promise<any>;
  getSourceMappings(params?: any): Promise<any>;
  updateSourceMapping(id: string, data: any): Promise<any>;
  deleteSourceMapping(id: string): Promise<any>;
  mapSourceToCustomer(sourceType: string, sourceId: string, customerId: string): Promise<any>;
  excludeSlackChannel(channelId: string): Promise<any>;
  unmapSourceFromCustomer(sourceType: string, sourceId: string): Promise<any>;
  getIntegrations(): Promise<any>;
  discoverSlackCustomers(): Promise<any>;
  joinSlackChannel(channelId: string): Promise<any>;
  connectIntegration(type: string, config: any): Promise<any>;
  disconnectIntegration(type: string): Promise<any>;
  deleteIntegration(type: string): Promise<any>;
  getDashboardData(): Promise<any>;
  getInsights(params?: any): Promise<any>;
  createInsight(data: any): Promise<any>;
  getQBRReports(params?: any): Promise<any>;
  createQBRReport(qbrData: any): Promise<any>;
  getQBRReport(reportId: string): Promise<any>;
  updateQBRReport(reportId: string, updateData: any): Promise<any>;
  deleteQBRReport(reportId: string): Promise<any>;
  regenerateQBRReport(reportId: string): Promise<any>;
  finalizeQBRReport(reportId: string): Promise<any>;
  uploadQBRTemplate(file: File, name: string, description?: string): Promise<any>;
  getQBRTemplates(): Promise<any>;
  getQBRTemplate(templateId: string): Promise<any>;
  deleteQBRTemplate(templateId: string): Promise<any>;
  exportQBRToPDF(reportId: string, templateId?: string): Promise<any>;
  getTeamMembers(): Promise<any>;
  inviteTeamMember(email: string, role: string): Promise<any>;
  updateTeamMember(id: string, data: any): Promise<any>;
  removeTeamMember(id: string): Promise<any>;
  approveTeamMember(id: string): Promise<any>;
  updateTeamMemberRole(id: string, role: string): Promise<any>;
  getBillingInfo(): Promise<any>;
  updateBillingInfo(data: any): Promise<any>;
  createCheckoutSession(priceId: string): Promise<any>;
  createPortalSession(): Promise<any>;
  assignAdminPlan(tenantId: string): Promise<any>;
  getAnalyticsInsights(filters?: any): Promise<any>;
  getAnalyticsSummary(): Promise<any>;
  updateInsightStatus(insightId: string, status: string): Promise<any>;
  triggerDailyAnalytics(): Promise<any>;
  generateBriefing(customerId: string, sinceDate: string): Promise<any>;
  // Notification methods
  getNotifications(limit?: number, offset?: number, unreadOnly?: boolean): Promise<any>;
  getUnreadCount(): Promise<any>;
  markAsRead(notificationId: string): Promise<any>;
  markAllAsRead(): Promise<any>;
  deleteNotification(notificationId: string): Promise<any>;
  getNotificationPreferences(): Promise<any>;
  updateNotificationPreferences(preferences: any): Promise<any>;
  getChatSessions(): Promise<any>;
  getChatSession(sessionId: string): Promise<any>;
  sendChatMessage(body: { message: string; session_id?: string }): Promise<any>;
  sendChatAction(body: { session_id?: string; action: import('../types/chat').SuggestedAction }): Promise<any>;
  deleteChatSession(sessionId: string): Promise<any>;
  uploadCampaignCsv(customerId: string, file: File): Promise<any>;
  getCustomerCampaigns(customerId: string): Promise<any>;
}

// Types for API responses
export interface DataSummary {
  tenant_id?: string;
  total_customers?: number;
  total_sources?: number;
  unmapped_sources?: number;
  recent_activity?: number;
  messages_count: number;
  customers_count: number;
  sources_count?: number;
  insights_count: number;
  chat_items_count?: number;
  source_mappings_count: number;
  qbr_reports_count?: number;
  integrations_count: number;
  total_data_points: number;
  integration_data_counts?: Record<string, number>;
  data_roots_tracked?: number;
  integration_status?: {
    slack: boolean;
    gong: boolean;
    hubspot: boolean;
    salesforce: boolean;
  };
}

export interface DeleteResponse {
  success: boolean;
  message: string;
  status?: string;
}

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://edyt-backend-free-384187065513.europe-west1.run.app',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
}) as ExtendedAxiosInstance;

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Get token from localStorage or wherever you store it
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response?.status === 401) {
      // Unauthorized:
      // - For app-auth failures, we redirect to login
      // - For integration-level auth failures (e.g. Zendesk/Jira tokens), we let the caller handle it
      const requestUrl: string = String(error.config?.url || '');
      const isIntegrationRequest =
        requestUrl.includes('/api/integrations/') ||
        requestUrl.startsWith('/api/integrations/');

      if (!isIntegrationRequest) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API methods
export const apiMethods = {
  // Custom request method for flexible API calls
  async customRequest<T = any>(url: string, config?: any): Promise<T> {
    const { body, ...axiosConfig } = config || {};
    const response = await apiClient.request({
      url,
      data: body, // Convert 'body' to 'data' for axios
      ...axiosConfig
    });
    return response.data;
  },

  async transcribeVoice(audio: Blob): Promise<{ transcript: string; confidence?: number; detected_language?: string }> {
    const formData = new FormData();
    formData.append('audio', audio, 'voice.webm');

    const response = await apiClient.post('/api/insights/transcribe', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });
    return response.data;
  },

  // Data Management
  async getDataSummary(): Promise<DataSummary> {
    const response = await apiClient.get('/api/data-management/summary');
    return response.data;
  },

  async deleteAllMessages(): Promise<DeleteResponse> {
    const response = await apiClient.delete('/api/data-management/messages');
    return response.data;
  },

  async deleteAllCustomers(): Promise<DeleteResponse> {
    const response = await apiClient.delete('/api/data-management/customers');
    return response.data;
  },

  async deleteAllAccountData(): Promise<DeleteResponse> {
    const response = await apiClient.delete('/api/data-management/account');
    return response.data;
  },

  async exportTenantData(): Promise<Blob> {
    const response = await apiClient.get('/api/data-management/export', {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data;
  },

  async deleteCustomerData(customerId: string): Promise<DeleteResponse> {
    const response = await apiClient.delete(`/api/data-management/customers/${customerId}`);
    return response.data;
  },

  // Customers
  async getCustomers(params?: any) {
    const response = await apiClient.get('/api/customers/', { params });
    return response.data;
  },

  async createCustomer(customerData: any) {
    const response = await apiClient.post('/api/customers/', customerData);
    return response.data;
  },

  async updateCustomer(customerId: string, customerData: any) {
    const response = await apiClient.put(`/api/customers/${customerId}`, customerData);
    return response.data;
  },

  async deleteCustomer(customerId: string) {
    const response = await apiClient.delete(`/api/customers/${customerId}`);
    return response.data;
  },

  // QBR Reports
  async getQBRReports(params?: any) {
    const response = await apiClient.get('/api/qbr/', { params });
    return response.data;
  },

  async createQBRReport(qbrData: any) {
    const response = await apiClient.post('/api/qbr/', qbrData);
    return response.data;
  },

  async getQBRReport(reportId: string) {
    const response = await apiClient.get(`/api/qbr/${reportId}`);
    return response.data;
  },

  async updateQBRReport(reportId: string, updateData: any) {
    const response = await apiClient.put(`/api/qbr/${reportId}`, updateData);
    return response.data;
  },

  async deleteQBRReport(reportId: string) {
    const response = await apiClient.delete(`/api/qbr/${reportId}`);
    return response.data;
  },

  async regenerateQBRReport(reportId: string) {
    const response = await apiClient.post(`/api/qbr/${reportId}/regenerate`);
    return response.data;
  },

  async finalizeQBRReport(reportId: string) {
    const response = await apiClient.post(`/api/qbr/${reportId}/finalize`);
    return response.data;
  },

  // QBR Templates
  async uploadQBRTemplate(file: File, name: string, description?: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    if (description) {
      formData.append('description', description);
    }
    
    const response = await apiClient.post('/api/qbr/templates', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async getQBRTemplates() {
    const response = await apiClient.get('/api/qbr/templates');
    return response.data;
  },

  async getQBRTemplate(templateId: string) {
    const response = await apiClient.get(`/api/qbr/templates/${templateId}`);
    return response.data;
  },

  async deleteQBRTemplate(templateId: string) {
    const response = await apiClient.delete(`/api/qbr/templates/${templateId}`);
    return response.data;
  },

  async exportQBRToPDF(reportId: string, templateId?: string) {
    const response = await apiClient.post(
      `/api/qbr/${reportId}/export-pdf`,
      { template_id: templateId },
      { 
        responseType: 'blob',
        timeout: 120000 // 2 minutes timeout for PDF generation
      }
    );
    return response.data;
  },

  // Source Mappings
  async getSourceMappings(params?: any) {
    const response = await apiClient.get('/api/source-mappings/', { params });
    return response.data;
  },

  async updateSourceMapping(sourceId: string, mappingData: any) {
    const response = await apiClient.put(`/api/source-mappings/${sourceId}`, mappingData);
    return response.data;
  },

  async deleteSourceMapping(sourceId: string) {
    const response = await apiClient.delete(`/api/source-mappings/${sourceId}`);
    return response.data;
  },

  // Integrations
  async getIntegrations() {
    const response = await apiClient.get('/api/integrations/');
    return response.data;
  },

  async connectIntegration(integrationType: string, config: any) {
    const response = await apiClient.post(`/api/integrations/${integrationType}/connect`, config);
    return response.data;
  },

  async disconnectIntegration(integrationType: string) {
    const response = await apiClient.post(`/api/integrations/${integrationType}/disconnect`);
    return response.data;
  },

  // Dashboard
  async getDashboardData() {
    const response = await apiClient.get('/api/dashboard/');
    return response.data;
  },

  // Insights
  async getInsights(params?: any) {
    const response = await apiClient.get('/api/insights/', { params });
    return response.data;
  },

  async createInsight(insightData: any) {
    const response = await apiClient.post('/api/insights/', insightData);
    return response.data;
  },

  // Team Management
  async getTeamMembers() {
    const response = await apiClient.get('/api/team');
    return response.data;
  },

  async inviteTeamMember(email: string, role: string) {
    const response = await apiClient.post('/api/team/invite', { email, role });
    return response.data;
  },

  async updateTeamMember(userId: string, userData: any) {
    const response = await apiClient.put(`/api/team/${userId}`, userData);
    return response.data;
  },

  async removeTeamMember(userId: string) {
    const response = await apiClient.delete(`/api/team/${userId}`);
    return response.data;
  },

  // Billing
  async getBillingInfo() {
    const response = await apiClient.get('/api/billing/');
    return response.data;
  },

  async updateBillingInfo(billingData: any) {
    const response = await apiClient.put('/api/billing/', billingData);
    return response.data;
  },

  async createCheckoutSession(priceId: string) {
    const response = await apiClient.post('/api/billing/checkout', { price_id: priceId });
    return response.data;
  },

  async createPortalSession() {
    const response = await apiClient.post('/api/billing/portal');
    return response.data;
  }
};

// Add all methods to apiClient instance
(apiClient as any).customRequest = apiMethods.customRequest;
(apiClient as any).transcribeVoice = apiMethods.transcribeVoice;
(apiClient as any).getUnmappedSources = async () => {
  const response = await apiClient.get('/api/customers/unmapped_sources');
  // API returns array directly, not wrapped in object
  return response.data || []; 
};
  (apiClient as any).getCustomers = async (params?: any) => {
    const response = await apiClient.get('/api/customers/', { params });
    return response.data;
  };
  (apiClient as any).createCustomer = async (data: any) => {
    const response = await apiClient.post('/api/customers/', data);
    return response.data;
  };
  (apiClient as any).getCustomer = async (id: string) => {
    const response = await apiClient.get(`/api/customers/${id}`);
    return response.data;
  };
  (apiClient as any).updateCustomer = async (id: string, data: any) => {
    const response = await apiClient.put(`/api/customers/${id}`, data);
    return response.data;
  };
  (apiClient as any).getCustomerSources = async (id: string) => {
    const response = await apiClient.get(`/api/customers/${id}/sources`);
    return response.data;
  };
  (apiClient as any).deleteCustomer = async (id: string) => {
    const response = await apiClient.delete(`/api/customers/${id}`);
    return response.data;
  };
(apiClient as any).getSourceMappings = (params?: any) => apiClient.get('/api/source-mappings/', { params });
(apiClient as any).updateSourceMapping = (id: string, data: any) => apiClient.put(`/api/source-mappings/${id}`, data);
(apiClient as any).deleteSourceMapping = (id: string) => apiClient.delete(`/api/source-mappings/${id}`);
(apiClient as any).mapSourceToCustomer = (sourceType: string, sourceId: string, customerId: string) => 
  apiClient.put('/api/customers/map_source', null, {
    params: {
      source_type: sourceType,
      source_id: sourceId,
      customer_id: customerId
    }
  });
(apiClient as any).excludeSlackChannel = (channelId: string) => 
  apiClient.post('/api/discovery/exclude_channel', null, {
    params: {
      channel_id: channelId
    }
  });
(apiClient as any).unmapSourceFromCustomer = (sourceType: string, sourceId: string) =>
  apiClient.delete('/api/customers/unmap_source', {
    params: {
      source_type: sourceType,
      source_id: sourceId
    }
  });

// Add helper functions for source type conversion
(apiClient as any).getSourceTypeLabel = (sourceType: string) => {
  switch (sourceType.toLowerCase()) {
    case 'slack_channel':
      return 'Slack Channel';
    case 'email_domain':
      return 'Email Domain';
    case 'crm_account':
      return 'CRM Account';
    case 'jira_org':
      return 'Jira Organization';
    case 'zendesk_org':
      return 'Zendesk Organization';
    case 'trello_org':
      return 'Trello Board / Label';
    case 'linear_org':
      return 'Linear Team / Label';
    default:
      return sourceType;
  }
};
(apiClient as any).getIntegrations = async () => {
  const response = await apiClient.get('/api/integrations/');
  // The API returns the object we need directly in response.data
  return response.data; 
};
(apiClient as any).discoverSlackCustomers = async () => {
  const response = await apiClient.post('/api/discovery/slack');
  return response.data;
};
(apiClient as any).joinSlackChannel = (channelId: string) => 
  apiClient.post('/api/integrations/slack/join-channel', { channel_id: channelId });
(apiClient as any).connectIntegration = (type: string, config: any) => apiClient.post(`/api/integrations/${type}/connect`, config);
(apiClient as any).disconnectIntegration = (type: string) => apiClient.post(`/api/integrations/${type}/disconnect`);
(apiClient as any).deleteIntegration = (type: string) => apiClient.delete(`/api/integrations/${type}`);
(apiClient as any).getDashboardData = () => apiClient.get('/api/dashboard/');
(apiClient as any).getInsights = (params?: any) => apiClient.get('/api/insights/', { params });
(apiClient as any).createInsight = (data: any) => apiClient.post('/api/insights/', data);
(apiClient as any).getQBRReports = async (params?: any) => {
  const response = await apiClient.get('/api/qbr/', { params });
  return response.data;
};
(apiClient as any).createQBRReport = async (qbrData: any) => {
  const response = await apiClient.post('/api/qbr/', qbrData);
  return response.data;
};
(apiClient as any).getQBRReport = async (reportId: string) => {
  const response = await apiClient.get(`/api/qbr/${reportId}`);
  return response.data;
};
(apiClient as any).getQBRReportStatus = async (reportId: string) => {
  const response = await apiClient.get(`/api/qbr/${reportId}/status`);
  return response.data;
};
(apiClient as any).updateQBRReport = async (reportId: string, updateData: any) => {
  const response = await apiClient.put(`/api/qbr/${reportId}`, updateData);
  return response.data;
};
(apiClient as any).deleteQBRReport = async (reportId: string) => {
  const response = await apiClient.delete(`/api/qbr/${reportId}`);
  return response.data;
};
(apiClient as any).regenerateQBRReport = async (reportId: string) => {
  const response = await apiClient.post(`/api/qbr/${reportId}/regenerate`);
  return response.data;
};
(apiClient as any).finalizeQBRReport = async (reportId: string) => {
  const response = await apiClient.post(`/api/qbr/${reportId}/finalize`);
  return response.data;
};
(apiClient as any).getTeamMembers = async () => {
  const response = await apiClient.get('/api/team/members');
  // API returns array directly, not wrapped in object
  return response.data || [];
};
(apiClient as any).inviteTeamMember = (email: string, role: string) => apiClient.post('/api/team/invites', { email, role });
(apiClient as any).updateTeamMember = (id: string, data: any) => apiClient.put(`/api/team/members/${id}`, data);
(apiClient as any).removeTeamMember = (id: string) => apiClient.delete(`/api/team/members/${id}`);
(apiClient as any).approveTeamMember = (id: string) => apiClient.put(`/api/team/members/${id}/approve`);
(apiClient as any).updateTeamMemberRole = (id: string, role: string) => 
  apiClient.put(`/api/team/members/${id}/role`, { new_role: role });
(apiClient as any).getBillingInfo = () => apiClient.get('/api/billing/');
(apiClient as any).updateBillingInfo = (data: any) => apiClient.put('/api/billing/', data);
(apiClient as any).createCheckoutSession = (priceId: string) => apiClient.post('/api/billing/checkout', { price_id: priceId });
(apiClient as any).createPortalSession = () => apiClient.post('/api/billing/portal');
(apiClient as any).assignAdminPlan = (tenantId: string) => apiClient.post(`/api/admin/tenants/${tenantId}/assign-admin-plan`);
  (apiClient as any).getAnalyticsInsights = async (filters?: any) => {
    const response = await apiClient.get('/api/insights/analytics', { params: filters });
    return response.data;
  };
  (apiClient as any).getAnalyticsSummary = async () => {
    const response = await apiClient.get('/api/insights/analytics/summary');
    return response.data;
  };
  (apiClient as any).updateInsightStatus = (insightId: string, status: string) => 
    apiClient.put(`/api/insights/analytics/${insightId}/status`, { status });
  (apiClient as any).triggerDailyAnalytics = () => apiClient.post('/api/insights/analytics/trigger');
  (apiClient as any).generateBriefing = async (customerId: string, sinceDate: string) => {
    const response = await apiClient.post('/api/insights/generate-briefing', { customer_id: customerId, since_date: sinceDate });
    return response.data;
  };

  // Zendesk methods
  (apiClient as any).getZendeskStatus = () => apiClient.get('/api/integrations/zendesk/status');
  (apiClient as any).authorizeZendesk = () => apiClient.get('/api/integrations/zendesk/authorize');
  (apiClient as any).syncZendeskData = () => apiClient.post('/api/integrations/zendesk/sync');
  (apiClient as any).disconnectZendesk = () => apiClient.delete('/api/integrations/zendesk/disconnect');

  // Trello methods
  (apiClient as any).getTrelloStatus = () => apiClient.get('/api/integrations/trello/status');
  (apiClient as any).authorizeTrello = () => apiClient.get('/api/integrations/trello/authorize');
  (apiClient as any).getTrelloBoards = () => apiClient.get('/api/integrations/trello/boards');
  (apiClient as any).importTrello = (data: { board_ids?: string[]; import_all?: boolean }) =>
    apiClient.post('/api/integrations/trello/import', data);
  (apiClient as any).disconnectTrello = () => apiClient.delete('/api/integrations/trello/disconnect');
  (apiClient as any).getTrelloOrganizations = () => apiClient.get('/api/integrations/trello/organizations');
  (apiClient as any).mapTrelloToCustomer = (data: { organization_name: string; customer_id: string }) =>
    apiClient.post('/api/integrations/trello/map-customer', data);

  // Linear methods
  (apiClient as any).getLinearStatus = () => apiClient.get('/api/integrations/linear/status');
  (apiClient as any).authorizeLinear = () => apiClient.get('/api/integrations/linear/authorize');
  (apiClient as any).getLinearTeams = () => apiClient.get('/api/integrations/linear/teams');
  (apiClient as any).importLinear = (data: { team_ids?: string[]; import_all?: boolean }) =>
    apiClient.post('/api/integrations/linear/import', data);
  (apiClient as any).disconnectLinear = () => apiClient.delete('/api/integrations/linear/disconnect');
  (apiClient as any).getLinearOrganizations = () => apiClient.get('/api/integrations/linear/organizations');
  (apiClient as any).mapLinearToCustomer = (data: { organization_name: string; customer_id: string }) =>
    apiClient.post('/api/integrations/linear/map-customer', data);

  // ===================================================================
  // CUSTOMER MAPPING API (for Zendesk/Jira integration)
  // ===================================================================
  (apiClient as any).createCustomerMapping = (customerId: string, data: {
    type: string;
    external_id: string;
    external_name: string;
  }) => apiClient.post(`/api/customers/${customerId}/mappings`, data);
  
  (apiClient as any).getCustomerMappings = (customerId: string) => 
    apiClient.get(`/api/customers/${customerId}/mappings`);
  
  (apiClient as any).deleteCustomerMapping = (customerId: string, externalKey: string) =>
    apiClient.delete(`/api/customers/${customerId}/mappings/${externalKey}`);

  // Notification methods
  (apiClient as any).getNotifications = async (limit?: number, offset?: number, unreadOnly?: boolean) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    if (unreadOnly) params.append('unread_only', 'true');
    
    const response = await apiClient.get(`/api/notifications?${params.toString()}`);
    return response.data;
  };

  (apiClient as any).getUnreadCount = async () => {
    const response = await apiClient.get('/api/notifications/unread-count');
    return response.data;
  };

  (apiClient as any).markAsRead = async (notificationId: string) => {
    const response = await apiClient.put(`/api/notifications/${notificationId}/read`);
    return response.data;
  };

  (apiClient as any).markAllAsRead = async () => {
    const response = await apiClient.put('/api/notifications/mark-all-read');
    return response.data;
  };

  (apiClient as any).deleteNotification = async (notificationId: string) => {
    const response = await apiClient.delete(`/api/notifications/${notificationId}`);
    return response.data;
  };

  (apiClient as any).getNotificationPreferences = async () => {
    const response = await apiClient.get('/api/notifications/preferences');
    return response.data;
  };

  (apiClient as any).updateNotificationPreferences = async (preferences: any) => {
    const response = await apiClient.put('/api/notifications/preferences', preferences);
    return response.data;
  };

  // QBR Template methods
  (apiClient as any).uploadQBRTemplate = async (file: File, name: string, description?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    if (description) {
      formData.append('description', description);
    }
    
    const response = await apiClient.post('/api/qbr/templates', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  };

  (apiClient as any).getQBRTemplates = async () => {
    const response = await apiClient.get('/api/qbr/templates');
    return response.data;
  };

  (apiClient as any).getQBRTemplate = async (templateId: string) => {
    const response = await apiClient.get(`/api/qbr/templates/${templateId}`);
    return response.data;
  };

  (apiClient as any).deleteQBRTemplate = async (templateId: string) => {
    const response = await apiClient.delete(`/api/qbr/templates/${templateId}`);
    return response.data;
  };

  (apiClient as any).exportQBRToPDF = async (reportId: string, templateId?: string) => {
    const response = await apiClient.post(
      `/api/qbr/${reportId}/export-pdf`,
      { template_id: templateId },
      { 
        responseType: 'blob',
        timeout: 120000 // 2 minutes timeout for PDF generation
      }
    );
    return response.data;
  };

  (apiClient as any).getChatSessions = async () => {
    const response = await apiClient.get('/api/chat/sessions');
    return response.data;
  };

  (apiClient as any).getChatSession = async (sessionId: string) => {
    const response = await apiClient.get(`/api/chat/sessions/${sessionId}`);
    return response.data;
  };

  (apiClient as any).sendChatMessage = async (body: { message: string; session_id?: string }) => {
    const response = await apiClient.post('/api/chat/message', body);
    return response.data;
  };

  (apiClient as any).sendChatAction = async (body: {
    session_id?: string;
    action: import('../types/chat').SuggestedAction;
  }) => {
    const response = await apiClient.post('/api/chat/action', body);
    return response.data;
  };

  (apiClient as any).deleteChatSession = async (sessionId: string) => {
    const response = await apiClient.delete(`/api/chat/sessions/${sessionId}`);
    return response.data;
  };

  (apiClient as any).uploadCampaignCsv = async (customerId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/api/customers/${customerId}/campaigns/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  };

  (apiClient as any).getCustomerCampaigns = async (customerId: string) => {
    const response = await apiClient.get(`/api/customers/${customerId}/campaigns`);
    return response.data;
  };

export { apiClient };
export default apiClient;
